/**
 * Selection: what surfaces next, and why.
 *
 * Selection is where the regime's dramaturgy is felt most directly. Echo Well
 * increases gravity toward what you already dwelt on; Crisis Swarm floods with
 * novelty. The `reason` on each scored candidate is what the work exposes when it
 * "shows its own seams" (spine §4).
 */
import { fragmentState } from './model.js';
/** The participant's current top tactics, by accumulated dwell. */
export function dominantTactics(session) {
    return Object.entries(session.tacticDwell)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([t]) => t);
}
/**
 * Score candidate fragments for surfacing in a session under a regime.
 * Higher score = more likely to appear. Deterministic given the RNG (jitter).
 */
export function scoreCandidates(candidates, session, regime, rng) {
    const seen = new Set(session.seen);
    const top = dominantTactics(session);
    const topSet = new Set(top.slice(0, 2));
    return candidates
        .map((fragment) => {
        let score = 0;
        const reasons = [];
        // Vitality: living fragments surface more readily.
        score += fragment.vitality * 1.0;
        // Echo gravity: pull toward tactics the participant keeps returning to.
        if (topSet.has(fragment.tactic)) {
            const g = regime.selection.echoGravity;
            score += g;
            if (g > 0.6)
                reasons.push(`you keep returning to ${fragment.tactic}`);
        }
        // Novelty: reward unseen fragments.
        if (!seen.has(fragment.id)) {
            score += regime.selection.noveltyPull;
            if (regime.selection.noveltyPull > 0.6)
                reasons.push('something you have not seen');
        }
        else {
            score -= 0.3; // mild penalty for repetition, unless echo gravity overrides
        }
        // Tactic bias from the regime.
        const tb = regime.tacticBias[fragment.tactic];
        if (tb && tb !== 1) {
            score *= tb;
            if (tb > 1)
                reasons.push(`the field is leaning on ${fragment.tactic}`);
        }
        // Small deterministic jitter so ties break reproducibly but not rigidly.
        score += rng.next() * 0.05;
        const reason = reasons[0] ?? `${fragmentState(fragment)} fragment`;
        return { fragment, score, reason };
    })
        .sort((a, b) => b.score - a.score);
}
/**
 * Choose the next fragments to surface to fill the field to the regime's target,
 * given what is already live.
 */
export function surfaceNext(candidates, liveCount, session, regime, rng) {
    const need = Math.max(0, regime.selection.fieldTarget - liveCount);
    if (need === 0)
        return [];
    return scoreCandidates(candidates, session, regime, rng).slice(0, need);
}
//# sourceMappingURL=selection.js.map